import { useMemo, useRef, useEffect, useState } from "react";
import { getRecordingInfo } from "../../../shared/utils/recordingHelper";
import PlayerConstants from "../../../shared/constants/player";

const { LIVE } = PlayerConstants.PLAYER_TYPE;

export const useRecordingTimes = (isRecording, recordingDeltaTime, setRemainingTime) => {
  /*
    recordingAssetElapsedTime and recordingAssetRemainingTime are state variables to store user specific recording asset elapsed time and remaining time.
    They are useful for user specific recording playback progress calculation,
    relative to user specific recording playback startDeltaTime and duration(recordingStopDeltaTime - recordingStartDeltaTime)
    distinguished from the actual elapsedTime/remainingTime relative to full recording playback startTime(recordingStartTime) and duration
  */
  const [recordingAssetElapsedTime, setRecordingAssetElapsedTime] = useState("");
  const [recordingAssetRemainingTime, setRecordingAssetRemainingTime] = useState(null);
  /** Dynamic partial recording playback start flag */
  const recordingLiveAssetStarted = useRef(false);

  /* Memoized recordingStartDeltaTime. This variable is to determine recording playback's initial playhead, calculated using startDeltaTime property of metadata from milliseconds to seconds */
  const recordingStartDeltaTime = useMemo(() => {
    if (isRecording && recordingDeltaTime) {
      return recordingDeltaTime.startDeltaTime > 0 &&
        recordingDeltaTime.recordingStartTime >= recordingDeltaTime.programStartTime
        ? recordingDeltaTime.startDeltaTime / 1000
        : 1; // startOffset in Bitmovin player SourceConfigOptions is required to be positive, so playback shifts/seeks to at least 1 second into user recorded portion. This is acceptable as per discussion with architect.
    }
    return null;
  }, [recordingDeltaTime, isRecording]);

  /* Memoized recordingStopDeltaTime. This variable is to determine recording playback's actual duration, calculated using stopDeltaTime property of metadata from milliseconds to seconds */
  const recordingStopDeltaTime = useMemo(() => {
    if (isRecording && recordingDeltaTime) {
      return recordingDeltaTime.stopDeltaTime / 1000;
    }
    return null;
  }, [recordingDeltaTime, isRecording]);

  /* Memoized user specific recording start time. */
  const userRecordingStartTime = useMemo(() => {
    if (isRecording && recordingDeltaTime) {
      return (
        recordingDeltaTime.recordingStartTime +
        (recordingDeltaTime.startDeltaTime > 0 ? recordingDeltaTime.startDeltaTime : 1000)
      ); // minimum 1 second from user's recording start delta, see recordingStartDeltaTime variable definition
    }
    return null;
  }, [recordingDeltaTime, isRecording]);

  const userRecordingStartTimeRef = useRef(userRecordingStartTime);

  /* Memoized user specific recording stop time. */
  const userRecordingStopTime = useMemo(() => {
    if (isRecording && recordingDeltaTime) {
      return recordingDeltaTime.recordingStartTime + recordingDeltaTime.stopDeltaTime;
    }
    return null;
  }, [recordingDeltaTime, isRecording]);

  const userRecordingStopTimeRef = useRef(userRecordingStopTime);

  const recordingDuration = useMemo(() => {
    const startTime = recordingDeltaTime?.startDeltaTime ?? 0;
    if (!recordingStopDeltaTime) return 0;
    return recordingStopDeltaTime - startTime / 1000;
  }, [recordingDeltaTime?.startDeltaTime, recordingStopDeltaTime]);

  // Due to the nature of live stream segments and recording prepadding, the duration calculated from recordingStartDeltaTime and recordingStopDeltaTime is not accurate. This variable is to determine the actual duration of the recording playback.
  const recordingPlaybackDuration = useRef(recordingDuration);

  const hasRecordingDeltaTime = recordingStartDeltaTime !== null && recordingStopDeltaTime !== null;

  useEffect(() => {
    if (recordingDuration && recordingDuration > 0) {
      setRemainingTime(recordingDuration);
    }
  }, [recordingDuration, setRemainingTime]);

  return {
    recordingStartDeltaTime,
    recordingStopDeltaTime,
    userRecordingStartTime,
    userRecordingStopTime,
    recordingDuration,
    recordingPlaybackDuration,
    hasRecordingDeltaTime,
    recordingAssetElapsedTime,
    setRecordingAssetElapsedTime,
    recordingAssetRemainingTime,
    setRecordingAssetRemainingTime,
    recordingLiveAssetStarted,
    userRecordingStartTimeRef,
    userRecordingStopTimeRef,
  };
};

export const useRecordingInfo = (
  type,
  isRecordingEnabled,
  recordingsList,
  liveProgramDetails,
  appProvider,
  isMR,
  manipulatedRecordingParams,
  isTeamRecordingsEnabled
) => {
  const recordingInfo = useMemo(() => {
    if (type === LIVE && isRecordingEnabled && recordingsList && liveProgramDetails?.metadata?.isRecordable) {
      return getRecordingInfo(
        isMR,
        liveProgramDetails,
        recordingsList,
        appProvider?.channelMapId,
        false,
        false,
        type,
        isTeamRecordingsEnabled
      );
    }

    return null;
  }, [type, isRecordingEnabled, recordingsList, liveProgramDetails, isMR, appProvider, isTeamRecordingsEnabled]);

  const showRecordingCTALoading = useMemo(() => {
    return (
      manipulatedRecordingParams &&
      recordingInfo &&
      manipulatedRecordingParams.targetAssetInfo?.id === recordingInfo.assetToRecord?.id
    );
  }, [manipulatedRecordingParams, recordingInfo]);

  return { recordingInfo, showRecordingCTALoading };
};
